import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import AdminLayout from '../../layouts/AdminLayout';
import axios from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Cart = () => {
    const { t } = useTranslation();
    const { cart, removeFromCart, clearCart, updateQuantity } = useCart();
    const navigate = useNavigate();

    const [address, setAddress] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
    const [notes, setNotes] = useState('');
    const [wantTransport, setWantTransport] = useState(false);
    
    // Adres Yönetimi State'leri
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState('new');
    const [saveAddress, setSaveAddress] = useState(false);
    const [addressTitle, setAddressTitle] = useState('');

    // Kayıtlı adresleri çek
    useEffect(() => {
        const fetchAddresses = async () => {
            try {
                const res = await axios.get('/users/addresses');
                setSavedAddresses(res.data);
                // Eğer kayıtlı adres varsa ilkini seçili yapabiliriz (isteğe bağlı)
                // if (res.data.length > 0) { setSelectedAddressId(res.data[0].id); setAddress(res.data[0].address); }
            } catch (error) {
                console.error('Adresler yüklenemedi');
            }
        };
        fetchAddresses();
    }, []);

    // Toplam Tutar Hesaplama
    const subTotal = cart.reduce((acc, item) => acc + (Number(item.price) * Number(item.quantity)), 0);

    // Nakliye Hesaplama
    let shippingCost = 0;
    if (wantTransport) {
        cart.forEach(item => {
            // Ürünün nakliye bedeli varsa hesapla
            const fee = Number(item.product.transport_fee) || 0;
            shippingCost += fee * Number(item.quantity);
        });
    }

    const finalTotal = subTotal + shippingCost;

    // Adres Seçimi Değiştiğinde
    const handleAddressSelect = (e) => {
        const val = e.target.value;
        setSelectedAddressId(val);
        if (val === 'new') {
            setAddress('');
        } else {
            const addr = savedAddresses.find(a => a.id === Number(val));
            if (addr) setAddress(addr.address);
        }
    };

    // Siparişi Tamamla
    const handleCheckout = async () => {
        if (!address) {
            toast.error(t('messages.enter_address'));
            return;
        }

        if (cart.length === 0) {
            toast.error(t('messages.cart_empty'));
            return;
        }

        try {
            // Eğer "Adresi Kaydet" seçiliyse ve yeni adres ise önce adresi kaydet
            if (selectedAddressId === 'new' && saveAddress) {
                if (!addressTitle) {
                    toast.error(t('messages.enter_address_title'));
                    return;
                }
                await axios.post('/users/addresses', { title: addressTitle, address });
                // Adres kaydedildi, siparişe devam et
            }

            const orderData = {
                items: cart,
                shipping_cost: shippingCost,
                payment_method: paymentMethod,
                shipping_address: address,
                notes: notes
            };

            const res = await axios.post('/orders', orderData);
            
            toast.success(t('messages.order_created'));
            clearCart(); // Sepeti boşalt
            navigate('/dashboard'); // Ana sayfaya yönlendir
            
        } catch (error) {
            toast.error(error.response?.data?.message || t('messages.order_create_error'));
        }
    };

    return (
        <AdminLayout>
            <h2>{t('cart.title')}</h2>
            
            {cart.length === 0 ? (
                <div className="alert alert-info">{t('cart.empty')}</div>
            ) : (
                <div className="row">
                    {/* Sol Taraf: Ürün Listesi */}
                    <div className="col-md-8">
                        <div className="card shadow-sm mb-4">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t('cart.product')}</th>
                                            <th>{t('cart.variation')}</th>
                                            <th>{t('cart.quantity')}</th>
                                            <th>{t('cart.unit_price')}</th>
                                            <th>{t('cart.total')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.product.name}</td>
                                                <td>{item.variation.size} - {item.variation.color}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <input 
                                                            type="number" 
                                                            className="form-control form-control-sm text-center me-2" 
                                                            style={{ width: '70px' }}
                                                            value={item.quantity}
                                                            min={item.product.min_order_limit || 1}
                                                            onChange={(e) => updateQuantity(item.product.id, item.variation?.id, e.target.value)}
                                                        />
                                                        <span className="text-muted small">{item.product.unit_type}</span>
                                                    </div>
                                                </td>
                                                <td>{Number(item.price).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</td>
                                                <td>{(item.price * item.quantity).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</td>
                                                <td>
                                                    <button 
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => removeFromCart(item.product.id, item.variation.id)}
                                                    >
                                                        {t('cart.delete')}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Adres ve Ödeme Bilgileri */}
                        <div className="card shadow-sm">
                            <div className="card-header bg-light">{t('cart.delivery_payment')}</div>
                            <div className="card-body">
                                {/* Adres Seçimi */}
                                {savedAddresses.length > 0 && (
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">{t('cart.saved_addresses')}</label>
                                        <select className="form-select" value={selectedAddressId} onChange={handleAddressSelect}>
                                            <option value="new">{t('cart.new_address_option')}</option>
                                            {savedAddresses.map(addr => (
                                                <option key={addr.id} value={addr.id}>{addr.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label fw-bold">{t('cart.delivery_address')}</label>
                                    <textarea 
                                        className="form-control" 
                                        rows="3" 
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        placeholder={t('cart.address_placeholder')}
                                        disabled={selectedAddressId !== 'new'} // Kayıtlı adres seçiliyse düzenlenemesin
                                    ></textarea>
                                </div>

                                {/* Yeni Adres Kaydetme Seçeneği */}
                                {selectedAddressId === 'new' && (
                                    <div className="mb-3 p-3 bg-light rounded border">
                                        <div className="form-check mb-2">
                                            <input className="form-check-input" type="checkbox" id="saveAddress" checked={saveAddress} onChange={(e) => setSaveAddress(e.target.checked)} />
                                            <label className="form-check-label" htmlFor="saveAddress">{t('cart.save_address')}</label>
                                        </div>
                                        {saveAddress && (
                                            <input 
                                                type="text" 
                                                className="form-control form-control-sm mt-2" 
                                                placeholder={t('cart.address_title_placeholder')} 
                                                value={addressTitle}
                                                onChange={(e) => setAddressTitle(e.target.value)}
                                            />
                                        )}
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">{t('cart.order_note')}</label>
                                    <textarea 
                                        className="form-control" 
                                        rows="2" 
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    ></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('cart.payment_method')}</label>
                                    <select 
                                        className="form-select" 
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                    >
                                        <option value="bank_transfer">{t('cart.bank_transfer')}</option>
                                        <option value="cash_on_delivery">{t('cart.cash_on_delivery')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sağ Taraf: Özet */}
                    <div className="col-md-4">
                        <div className="card shadow-sm">
                            <div className="card-header bg-primary text-white">{t('cart.order_summary')}</div>
                            <div className="card-body">
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{t('cart.subtotal')}:</span>
                                    <strong>{subTotal.toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</strong>
                                </div>
                                
                                <div className="form-check mb-3 border-bottom pb-3">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="transportCheck"
                                        checked={wantTransport}
                                        onChange={(e) => setWantTransport(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="transportCheck">
                                        {t('cart.want_transport')}
                                    </label>
                                </div>

                                {wantTransport && (
                                    <div className="d-flex justify-content-between mb-2 text-muted">
                                        <span>{t('cart.shipping_cost')}:</span>
                                        <span>{shippingCost.toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</span>
                                    </div>
                                )}

                                <div className="d-flex justify-content-between mt-3 pt-3 border-top">
                                    <span className="fs-5">{t('cart.grand_total')}:</span>
                                    <span className="fs-4 fw-bold text-success">
                                        {finalTotal.toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}
                                    </span>
                                </div>

                                <button 
                                    className="btn btn-success w-100 mt-4 py-2"
                                    onClick={handleCheckout}
                                >
                                    {t('cart.confirm_order')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default Cart;
